import Editor from '@monaco-editor/react';
import { MdCode } from 'react-icons/md';
import { useEffect, useRef, useCallback } from 'react';
import { matlabLanguageDefinition, matlabLanguageConfiguration, matlabCompletionProvider } from './languages/matlab';
import './AIFeedback.css';

const EditorPane = ({
  code,
  language,
  theme,
  onChange,
  activeFile,
  settings = {},
  feedbackItems = [],
  showAIFeedbackPane = false,
  onEditorReady = null // Callback to pass editor instance to parent
}) => {
  const editorRef = useRef(null);
  const currentDecorations = useRef([]);

  // Ensure code is always a string
  const safeCode = typeof code === 'string' ? code : (code || '');
  const safeLanguage = typeof language === 'string' ? language : 'javascript';
  const safeTheme = settings.theme || (typeof theme === 'string' ? theme : 'vs-dark');

  // Merge default settings with provided settings
  const editorSettings = {
    // Appearance & Theme defaults
    theme: 'vs-dark',
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: 14,
    fontLigatures: true,
    lineHeight: 1.5,
    letterSpacing: 0,
    minimapEnabled: false,
    minimapRenderCharacters: true,
    minimapMaxColumn: 120,
    renderWhitespace: 'none',
    renderControlCharacters: false,
    cursorStyle: 'line',
    cursorBlinking: 'blink',
    // Editor Behavior defaults
    wordWrap: 'on',
    wordWrapColumn: 80,
    lineNumbers: 'on',
    tabSize: 4,
    insertSpaces: true,
    autoClosingBrackets: 'always',
    autoClosingQuotes: 'always',
    autoClosingDelete: 'auto',
    autoIndent: 'full',
    folding: true,
    foldingHighlight: true,
    codeLens: true,
    quickSuggestions: true,
    parameterHintsEnabled: true,
    acceptSuggestionOnEnter: 'on',
    // Layout & View defaults
    rulers: [],
    scrollBeyondLastLine: false,
    smoothScrolling: true,
    stickyScrollEnabled: false,
    ...settings
  };


  // Only log when there are actual issues
  if (typeof code !== 'string' && code !== null && code !== undefined) {
    console.warn('EditorPane received non-string code:', typeof code, code);
  }

  // Update AI feedback decorations - inline annotations only (hidden when pane is open)
  const updateFeedbackDecorations = useCallback((editor, monaco) => {
    console.log('🎨 updateFeedbackDecorations called', {
      hasEditor: !!editor,
      hasFeedbackItems: !!feedbackItems,
      feedbackCount: feedbackItems?.length,
      showAIFeedbackPane
    });

    if (!editor || !feedbackItems) {
      console.log('❌ Missing editor or feedbackItems');
      return;
    }

    // Clear existing decorations
    currentDecorations.current = editor.deltaDecorations(currentDecorations.current, []);

    // Don't show inline decorations when feedback pane is open
    if (showAIFeedbackPane) {
      console.log('ℹ️ Feedback pane is open - hiding inline decorations');
      return;
    }

    if (feedbackItems.length === 0) {
      console.log('❌ No feedback items');
      return;
    }

    const model = editor.getModel();
    if (!model) {
      console.log('❌ No editor model');
      return;
    }

    console.log('✅ Creating decorations for', feedbackItems.length, 'items');

    // Create new decorations
    const newDecorations = feedbackItems.map(item => {
      const lineCount = model.getLineCount();
      const lineNumber = Math.max(1, Math.min(item.line, lineCount));
      const lineContent = model.getLineContent(lineNumber);
      const lineLength = lineContent.length;

      console.log(`  Line ${lineNumber}: "${lineContent}" (length: ${lineLength})`);
      console.log(`  Message: "${item.message}"`);

      const decoration = {
        range: new monaco.Range(lineNumber, 1, lineNumber, lineLength + 1),
        options: {
          after: {
            content: `    // ${item.message}`,
            inlineClassName: 'ai-feedback-inline'
          }
        }
      };

      console.log('  Decoration:', decoration);
      return decoration;
    });

    console.log('📍 Applying', newDecorations.length, 'decorations');

    // Apply new decorations
    const decorationIds = editor.deltaDecorations([], newDecorations);
    currentDecorations.current = decorationIds;

    console.log('✅ Decorations applied, IDs:', decorationIds);
  }, [feedbackItems, showAIFeedbackPane]);

  const handleEditorMount = (editor, monaco) => {
    console.log('Monaco Editor mounted successfully');
    editorRef.current = editor;

    // Always register MATLAB language support (not just when needed)
    if (!monaco.languages.getLanguages().some(lang => lang.id === 'matlab')) {
      console.log('Registering MATLAB language support');

      // Register MATLAB as a language
      monaco.languages.register({ id: 'matlab' });

      // Set the language configuration
      monaco.languages.setLanguageConfiguration('matlab', matlabLanguageConfiguration);

      // Set the monarch tokenizer
      monaco.languages.setMonarchTokensProvider('matlab', matlabLanguageDefinition);

      // Register completion provider
      monaco.languages.registerCompletionItemProvider('matlab', {
        ...matlabCompletionProvider,
        provideCompletionItems: (model, position) => matlabCompletionProvider.provideCompletionItems(model, position, monaco)
      });

      console.log('MATLAB language registered successfully');
    }

    // Set up AI feedback decorations
    updateFeedbackDecorations(editor, monaco);

    // Notify parent that editor is ready (but parent won't call layout - Monaco handles it)
    if (onEditorReady) {
      onEditorReady(editor);
    }

    // Debug: Log current language
    console.log('Current editor language:', safeLanguage);
  };

  // Update decorations when feedback items or pane visibility changes
  useEffect(() => {
    if (editorRef.current && window.monaco) {
      updateFeedbackDecorations(editorRef.current, window.monaco);
    }
  }, [feedbackItems, showAIFeedbackPane, updateFeedbackDecorations]);


  // Show welcome screen when no file is active
  if (!activeFile) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <MdCode className="mx-auto text-4xl mb-4 text-gray-400" />
          <h3 className="text-lg font-medium mb-2">Welcome to Code Editor</h3>
          <p className="text-sm">Create a new file or open an existing one to start coding</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <Editor
          height="100%"
          language={safeLanguage}
          value={safeCode}
          theme={safeTheme}
          onChange={onChange}
          onMount={handleEditorMount}
          loading={<div className="flex items-center justify-center h-full">Loading Monaco Editor...</div>}
          options={{
            // Basic options
            selectOnLineNumbers: true,
            automaticLayout: true, // Let Monaco handle layout automatically
            glyphMargin: false, // Glyph margin disabled (not using in-editor feedback)

            // Appearance & Theme settings
            fontFamily: editorSettings.fontFamily,
            fontSize: editorSettings.fontSize,
            fontLigatures: editorSettings.fontLigatures,
            lineHeight: editorSettings.lineHeight,
            letterSpacing: editorSettings.letterSpacing,
            minimap: {
              enabled: editorSettings.minimapEnabled,
              renderCharacters: editorSettings.minimapRenderCharacters,
              maxColumn: editorSettings.minimapMaxColumn
            },
            renderWhitespace: editorSettings.renderWhitespace,
            renderControlCharacters: editorSettings.renderControlCharacters,
            cursorStyle: editorSettings.cursorStyle,
            cursorBlinking: editorSettings.cursorBlinking,

            // Editor Behavior settings
            wordWrap: editorSettings.wordWrap,
            wordWrapColumn: editorSettings.wordWrapColumn,
            lineNumbers: editorSettings.lineNumbers,
            tabSize: editorSettings.tabSize,
            insertSpaces: editorSettings.insertSpaces,
            autoClosingBrackets: editorSettings.autoClosingBrackets,
            autoClosingQuotes: editorSettings.autoClosingQuotes,
            autoClosingDelete: editorSettings.autoClosingDelete,
            autoIndent: editorSettings.autoIndent,
            folding: editorSettings.folding,
            foldingHighlight: editorSettings.foldingHighlight,
            codeLens: editorSettings.codeLens,
            acceptSuggestionOnEnter: editorSettings.acceptSuggestionOnEnter,

            // Layout & View settings
            rulers: editorSettings.rulers,
            scrollBeyondLastLine: editorSettings.scrollBeyondLastLine,
            smoothScrolling: editorSettings.smoothScrolling,
            stickyScroll: {
              enabled: editorSettings.stickyScrollEnabled
            },

            // Advanced suggestion settings
            formatOnPaste: true,
            formatOnType: true,
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnCommitCharacter: true,
            tabCompletion: 'on',
            parameterHints: {
              enabled: editorSettings.parameterHintsEnabled,
              cycle: true
            },
            suggest: {
              showIcons: true,
              showSnippets: true,
              showWords: true,
              showStatusBar: true,
              insertMode: 'insert',
              filterGraceful: true,
              snippetsPreventQuickSuggestions: false,
              localityBonus: true,
              shareSuggestSelections: true,
              showInlineDetails: true
            },
            quickSuggestions: editorSettings.quickSuggestions ? {
              other: true,
              comments: true,
              strings: true
            } : false,
            quickSuggestionsDelay: 10,
            suggestSelection: 'recentlyUsed',
            bracketPairColorization: {
              enabled: true
            },
            guides: {
              indentation: true,
              highlightActiveIndentation: true,
              bracketPairs: true,
              bracketPairsHorizontal: true
            },
            inlineSuggest: {
              enabled: true
            },
            semanticHighlighting: {
              enabled: true
            }
          }}
        />
    </div>
  );
};

export default EditorPane;